import React, { useState, useRef, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '../../hooks/useAppDispatch.js'
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from 'framer-motion'
import { IoClose, IoMenuOutline } from 'react-icons/io5'
import { IoIosArrowDown } from 'react-icons/io'
import { IoArrowForward } from "react-icons/io5";
import { FaArrowRight, FaBriefcase, FaRegQuestionCircle, FaMapMarkerAlt } from "react-icons/fa";
import { openModal } from '../../redux/slices/modalSlice.js';
import { fetchBlogs } from '../../redux/slices/blogsSlice.js';
import { LazyImage } from '../common/LazyImage.jsx';
import LogiqLogo from '../../assets/Images/Logo/logo-logiq.svg';

import './PublicHeader.scss'

export default function PublicHeader() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(null)
  const [activeItem, setActiveItem] = useState(null)
  const [isHoveringDropdown, setIsHoveringDropdown] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1100)
  const [insightsFetched, setInsightsFetched] = useState(false)

  // -- SCROLL STATE MANAGEMENT --
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  
  // Ref detects scroll position instantly without waiting for re-renders
  const isScrolledRef = useRef(false);
  
  const { scrollY } = useScroll();

  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const { nav } = useAppSelector((state) => state.global)
  const { blogs, loading: blogsLoading } = useAppSelector((state) => state.blogs)

  const dropdownRef = useRef()
  const hoverTimeoutRef = useRef(null)

  const menuItems = [
    { key: 'services', title: 'Services', type: 'dropdown', link: '/services' },
    { key: 'industries', title: 'Industries', type: 'dropdown', link: '/industries' },
    { key: 'case-studies', title: 'Case Studies', type: 'link', link: '/case-studies' },
    { key: 'insights', title: 'Insights', type: 'link', link: '/blogs' },
    { key: 'company', title: 'Company', type: 'dropdown', link: '/contact' }
  ];

  // 1. Initial Mount Check (Fixes "No blur on refresh" issue)
  useEffect(() => {
    if (typeof window !== 'undefined' && window.scrollY > 50) {
      setIsScrolled(true);
      isScrolledRef.current = true;
    }
  }, []);

  // 2. Real-time Scroll Logic
  useMotionValueEvent(scrollY, "change", (latest) => {
    const previous = scrollY.getPrevious() || 0;
    
    // --- BLUR LOGIC ---
    // We use the Ref for comparison to prevent "flickering" during rapid scrolls
    const shouldBeScrolled = latest > 50;
    
    if (shouldBeScrolled !== isScrolledRef.current) {
      setIsScrolled(shouldBeScrolled);
      isScrolledRef.current = shouldBeScrolled;
    }

    // --- HIDE/SHOW LOGIC ---
    if (isMenuOpen) {
      setIsHidden(false);
      return;
    }

    if (latest > previous && latest > 150) {
      // Scrolling Down -> Hide
      if (!isHidden) setIsHidden(true);
    } else {
      // Scrolling Up -> Show
      if (isHidden) setIsHidden(false);
    }
  });

  // --- Existing Logic ---

  useEffect(() => {
    if (activeItem !== null && menuItems[activeItem]?.key === 'company' && !insightsFetched) {
      dispatch(fetchBlogs({ page: 1 }))
      setInsightsFetched(true)
    }
  }, [activeItem, dispatch, insightsFetched])

  const createDropdownContent = () => {
    const companyLinks = nav?.navItems?.find(item => item.key === 'company')?.links || []
    
    const latestInsights = blogs?.blogs?.slice(0, 2).map(blog => ({
      image: blog.hero?.imageUrl || "https://via.placeholder.com/150x85",
      title: blog.title || 'Untitled Blog',
      tech: blog.tags?.slice(0, 2) || blog.categories?.slice(0, 2) || ['Blog', 'Insights'],
      link: `/blog/${blog.slug}`,
      id: blog._id,
      publishedAt: blog.publishedAt || blog.createdAt
    })) || []

    const testimonialsData = nav?.testimonials || {}

    const companyItems = companyLinks.length > 0 ? companyLinks.map((link, index) => {
      let icon
      switch (link.label.toLowerCase()) {
        case 'about': icon = <FaRegQuestionCircle style={{ color: "var(--primary-deeper)" }} fontSize={14} />; break
        case 'careers': icon = <FaBriefcase style={{ color: "var(--primary-deeper)" }} fontSize={14} />; break
        case 'contact': icon = <FaMapMarkerAlt style={{ color: "var(--primary-deeper)" }} fontSize={14} />; break
        default: icon = <FaArrowRight style={{ color: "var(--primary-deeper)" }} fontSize={14} />
      }
      return {
        icon,
        title: link.label,
        desc: getCompanyItemDescription(link.label),
        link: link.path,
        id: index
      }
    }) : [
       { icon: <FaRegQuestionCircle fontSize={14}/>, title: 'About Us', desc: 'Our mission & story', link: '/about' },
       { icon: <FaBriefcase fontSize={14}/>, title: 'Careers', desc: 'Join our team', link: '/careers' },
       { icon: <FaMapMarkerAlt fontSize={14}/>, title: 'Contact', desc: 'Get in touch', link: '/contact' }
    ];

    const servicesItems = (nav?.navItems?.find(item => item.key === 'services')?.links || [])
      .slice(0, 8)
      .map((link, index) => ({
        icon: <FaArrowRight style={{ color: "var(--primary-deeper)" }} fontSize={12} />,
        title: link.label,
        desc: getServiceDescription(link.label),
        link: link.path,
        id: index
      }))

    const industriesItems = (nav?.navItems?.find(item => item.key === 'industries')?.links || [])
      .slice(0, 8)
      .map((link, index) => ({
        icon: <FaArrowRight style={{ color: "var(--primary-deeper)" }} fontSize={12} />,
        title: link.label,
        desc: getIndustryDescription(link.label),
        link: link.path,
        id: index
      }))

    return {
      services: {
        cols: [
          servicesItems.slice(0, Math.ceil(servicesItems.length / 2)),
          servicesItems.slice(Math.ceil(servicesItems.length / 2))
        ].filter(col => col.length > 0)
      },
      industries: {
        cols: [
          industriesItems.slice(0, Math.ceil(industriesItems.length / 2)),
          industriesItems.slice(Math.ceil(industriesItems.length / 2))
        ].filter(col => col.length > 0)
      },
      company: {
        type: "special",
        leftCol: companyItems,
        rightTitle: testimonialsData.heading || "Latest Insights",
        insights: latestInsights,
      }
    }
  }

  const getCompanyItemDescription = (label) => {
    const lower = label.toLowerCase()
    if (lower.includes('about')) return "Learn about our mission and values"
    if (lower.includes('career')) return "Join our talented team"
    if (lower.includes('contact')) return "Get in touch with our team"
    return "Learn more about our company"
  }

  const getServiceDescription = (label) => {
    return `Professional ${label.toLowerCase()} services`
  }

  const getIndustryDescription = (label) => {
    return `${label.toLowerCase()} industry solutions`
  }

  const dropdownContent = createDropdownContent()

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1100
      setIsMobile(mobile)
      if (mobile) setActiveItem(null)
      else {
        setIsMenuOpen(false)
        setMobileDropdownOpen(null)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleItemHover = (index) => {
    if (isMobile) return
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current)
    setActiveItem(index)
  }

  const handleItemLeave = () => {
     if (isMobile) return;
     hoverTimeoutRef.current = setTimeout(() => {
         if (!isHoveringDropdown) {
             setActiveItem(null)
         }
     }, 300)
  }

  const handleDropdownHover = () => {
    setIsHoveringDropdown(true)
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current)
  }

  const handleDropdownLeave = () => {
    setIsHoveringDropdown(false)
    // Add delay to prevent flicker when moving back up to nav item
    hoverTimeoutRef.current = setTimeout(() => {
        setActiveItem(null)
    }, 200)
  }

  const handleItemClick = (item) => {
    if(item.link) {
      if(isMobile && item.type === 'dropdown') {
        navigate(item.link);
        setActiveItem(null);
        setIsMenuOpen(false);
      } else {
        navigate(item.link);
        setActiveItem(null); // Close dropdown if open
      }
    }
  }

  const handleDesktopLinkClick = (link) => {
    navigate(link)
    setActiveItem(null)
    setIsHoveringDropdown(false)
  }

  const isSpecialLayout = activeItem !== null && menuItems[activeItem]?.key === 'company'

  const navVariants = {
    visible: { y: 0, opacity: 1 },
    hidden: { y: "-100%", opacity: 0 },
  };

  return (
    <motion.nav 
      className={`ph-navbar ${isScrolled ? "ph-scrolled" : ""}`}
      variants={navVariants}
      animate={isHidden ? "hidden" : "visible"}
      transition={{ duration: 0.35, ease: "easeInOut" }}
    >
      <div 
        className="ph-brand"
        onClick={() => {
          window.scrollTo({ top: 0, behavior: "smooth" })
          navigate("/")
        }}
      >
        <img src={LogiqLogo} alt="LogiqLink" />
      </div>

      {!isMobile && (
        <div className="ph-menu">
          {menuItems.map((item, index) => (
            <div 
                key={item.key}
                className={`ph-menu-item ${activeItem === index ? 'ph-active' : ''}`}
                onMouseEnter={() => handleItemHover(index)}
                onMouseLeave={handleItemLeave}
                onClick={() => handleItemClick(item)}
            >
              <span>
                  {item.title} 
                  {item.type === 'dropdown' && <IoIosArrowDown className="ph-arrow-icon" />}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="ph-actions">
        {isMobile && (
             <button
             className="ph-mobile-toggle"
             onClick={() => setIsMenuOpen(true)}
           >
             <IoMenuOutline />
           </button>
        )}

        {!isMobile && (
            <>
                <button className="ph-audit-btn" onClick={() => navigate('/contact')}>
                    Book Free Audit
                </button>
            </>
        )}
      </div>

      <AnimatePresence>
        {!isMobile && activeItem !== null && menuItems[activeItem].type === 'dropdown' && (
          <motion.div
            className="ph-dropdown-container"
            initial={{ opacity: 0, y: 10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: 10, height: 0 }}
            transition={{ duration: 0.2 }}
            ref={dropdownRef}
          >
             <div 
                className="ph-dropdown"
                onMouseEnter={handleDropdownHover}
                onMouseLeave={handleDropdownLeave}
             >
                  {isSpecialLayout ? (
                    <div className="ph-content-row ph-special-layout">
                      <div className="ph-col">
                        <div className="ph-title-row">
                          <h3>{menuItems[activeItem].title}</h3>
                        </div>
                        {dropdownContent[menuItems[activeItem].key]?.leftCol?.map(
                          (item, index) => (
                            <div
                              className="ph-item"
                              key={`left-item-${index}`}
                              onClick={() => handleDesktopLinkClick(item.link)}
                            >
                              <span className="ph-item-icon">
                                {item.icon}
                              </span>
                              <div className="ph-item-text">
                                <h4>{item.title}</h4>
                                <p>{item.desc}</p>
                              </div>
                            </div>
                          )
                        )}
                      </div>

                      <div className="ph-col ph-right-col">
                        <div className="ph-right-col-header">
                            <h3>
                              {dropdownContent[menuItems[activeItem].key]?.rightTitle}
                            </h3>
                            <button
                              className="ph-view-all-btn"
                              onClick={() => {
                                navigate("/blogs")
                                setActiveItem(null)
                              }}
                            >
                              View All
                              <IoArrowForward style={{ marginLeft: "5px" }} fontSize={15} />
                            </button>
                        </div>

                        <div className="ph-insights-list">
                          {blogsLoading ? (
                             <div className="loading-state">Loading...</div>
                          ) : dropdownContent[menuItems[activeItem].key]?.insights?.map(
                            (insight, index) => (
                              <div
                                key={`insight-${insight.id || index}`}
                                className="ph-insight-item"
                                onClick={() => handleDesktopLinkClick(insight.link)}
                              >
                                <div className="ph-insight-content">
                                  <div className="ph-insight-image">
                                    <LazyImage
                                      src={insight.image}
                                      alt={insight.title}
                                      className="ph-insight-image"
                                    />
                                  </div>
                                  <div className="ph-insight-details">
                                    <h4>{insight.title}</h4>
                                    <div className="ph-tech-capsules">
                                      {insight.tech.map((tech, techIndex) => (
                                        <span key={`tech-${techIndex}`} className="ph-tech-capsule">
                                          {tech}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="ph-content-row">
                        {dropdownContent[menuItems[activeItem].key]?.cols?.map((col, colIndex) => (
                             <div className="ph-col" key={colIndex}>
                                 {col.map((item, index) => (
                                     <div
                                        className="ph-item"
                                        key={`item-${colIndex}-${index}`}
                                        onClick={() => handleDesktopLinkClick(item.link)}
                                      >
                                          <span className="ph-item-icon">
                                            {item.icon}
                                          </span>
                                          <div className="ph-item-text">
                                            <h4>{item.title}</h4>
                                            <p>{item.desc}</p>
                                          </div>
                                      </div>
                                 ))}
                             </div>
                        ))}
                    </div>
                  )}
             </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isMobile && isMenuOpen && (
             <motion.div
             className="ph-mobile-overlay"
             initial={{ x: "100%" }}
             animate={{ x: 0 }}
             exit={{ x: "100%" }}
             transition={{ type: "tween", duration: 0.3 }}
           >
              <div className="ph-mobile-header">
                <div className="ph-brand">
                    <img src={LogiqLogo} alt="LogiqLink" />
                </div>
                <button className="ph-close-btn" onClick={() => setIsMenuOpen(false)}>
                    <IoClose />
                </button>
             </div>
             
             <div className="ph-mobile-links">
                {menuItems.map((item) => (
                    <div key={item.key} className="ph-mobile-link-group">
                        {item.type === 'dropdown' ? (
                            <>
                                <button 
                                    className="ph-mobile-link-btn"
                                    onClick={() => setMobileDropdownOpen(mobileDropdownOpen === item.key ? null : item.key)}
                                >
                                    {item.title}
                                    <IoIosArrowDown className={`ph-arrow ${mobileDropdownOpen === item.key ? 'ph-rotated' : ''}`} />
                                </button>
                                <AnimatePresence>
                                    {mobileDropdownOpen === item.key && (
                                        <motion.div className="ph-mobile-sublinks"
                                            initial={{ height: 0 }}
                                            animate={{ height: "auto" }}
                                            exit={{ height: 0 }}
                                        >
                                            {dropdownContent[item.key]?.cols?.flat().map((sub, i) => (
                                                <div key={i} className="ph-mobile-sublink" onClick={() => {
                                                    navigate(sub.link)
                                                    setIsMenuOpen(false)
                                                }}>
                                                    {sub.title}
                                                </div>
                                            ))}
                                            {dropdownContent[item.key]?.leftCol?.map((sub, i) => (
                                                <div key={i} className="ph-mobile-sublink" onClick={() => {
                                                     navigate(sub.link)
                                                     setIsMenuOpen(false)
                                                }}>
                                                    {sub.title}
                                                </div>
                                            ))}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </>
                        ) : (
                             <button 
                                className="ph-mobile-link-btn"
                                onClick={() => {
                                    navigate(item.link)
                                    setIsMenuOpen(false)
                                }}
                            >
                                {item.title}
                            </button>
                        )}
                    </div>
                ))}
             </div>
             <div className="ph-mobile-footer">
                <button className="ph-audit-btn ph-full-width" onClick={() => navigate('/contact')}>
                    Book Free Audit
                </button>
             </div>
           </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  )
}